import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useStore from '../../store/useStore';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';
import useDialogKeys from '../../hooks/useDialogKeys';
import WidgetFactory from '../widgets/WidgetFactory';
import PackLogo from '../data-sources/PackLogo';
import { previewOf, widgetPixelSize, PREVIEW_WIDGET_ID } from './editorUtils';

/** The dashboard's grid width, or the nearest estimate when it has no grid yet. */
function gridWidthNow() {
  const grid = document.querySelector('.dashboard-container .layout');
  return grid?.clientWidth || Math.max(320, window.innerWidth - 304);
}

/**
 * What a widget will look like before it is added: the dashboard's own widget,
 * with real data, at the size it will be added. Nothing is saved until Add.
 */
const WidgetPreview = ({ item, busy = false, onAdd, onClose }) => {
  const { widget, kind, widgetType } = useMemo(() => previewOf(item.action), [item.action]);
  const setWidgetPeriod = useStore((state) => state.setWidgetPeriod);
  const overlay = useModalOverlayClose(onClose);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [size, setSize] = useState(() => widgetPixelSize(kind, widgetType, gridWidthNow(), window.innerWidth));
  useDialogKeys(dialogRef, onClose);

  useEffect(() => {
    closeRef.current?.focus();
    const onResize = () => setSize(widgetPixelSize(kind, widgetType, gridWidthNow(), window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      // A period tried out here belongs to the preview only.
      setWidgetPeriod(PREVIEW_WIDGET_ID, null);
    };
  }, [kind, widgetType, setWidgetPeriod]);

  const add = async () => {
    await onAdd(item.action, item.key);
    onClose();
  };

  // Titles and KPI cards lead to other pages, which would leave the editor. Everything else works as it will on the dashboard.
  const stayHere = (e) => {
    if (e.target.closest?.('.widget-title, .scorecard-tile')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return createPortal(
    <div className="modal-overlay" {...overlay}>
      <div className="widget-preview" role="dialog" aria-modal="true" aria-labelledby="widget-preview-title" ref={dialogRef} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <header className="widget-preview-header">
          <div>
            <h2 id="widget-preview-title">Preview: {item.title}</h2>
            <p>Your live data, at the size it will be added. Nothing is saved until you add it.</p>
          </div>
          <button type="button" className="editor-drawer-close" ref={closeRef} onClick={onClose} aria-label="Close preview">×</button>
        </header>
        <div className="widget-preview-canvas">
          <div className="widget-preview-frame" data-testid="widget-preview-frame" style={{ width: size.width, height: size.height }} onClickCapture={stayHere}>
            <WidgetFactory widget={widget} />
          </div>
        </div>
        <footer className="widget-preview-footer">
          <span className="library-card-tags">
            {item.pack && <span className="library-pack-chip"><PackLogo id={item.pack.id} title={item.pack.title} size="tiny" />{item.pack.title}</span>}
            {item.tag && <span className="library-tag">{item.tag}</span>}
            {item.added && <span className="library-tag library-tag--on">On dashboard</span>}
          </span>
          <span className="widget-preview-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
            <button type="button" className="btn-primary" disabled={busy} onClick={add}>{busy ? 'Adding…' : item.added ? 'Add again' : 'Add to dashboard'}</button>
          </span>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default WidgetPreview;
